import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UnifiedUploadModal from '../../components/UnifiedUploadModal';
import { getRotations } from '../../services/apiService';
import './calendar.css';

const localizer = momentLocalizer(moment);

const CalendarView = () => {
  const navigate = useNavigate();
  const [rotations, setRotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [view, setView] = useState('month');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { label: 'Calendar', icon: 'Calendar' }
  ];

  useEffect(() => {
    loadRotations();
  }, []); // Only load once on mount

  const loadRotations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🗓️ [CALENDAR] Loading rotations...');

      // Fetch all rotations from API (no date filtering on backend yet)
      const data = await getRotations();
      
      console.log('🗓️ [CALENDAR] Raw API response:', data);
      console.log('🗓️ [CALENDAR] Response structure:', {
        hasRotations: !!data?.rotations,
        rotationsType: Array.isArray(data?.rotations) ? 'array' : typeof data?.rotations,
        total: data?.total,
        limit: data?.limit
      });
      
      // API returns { total, limit, offset, rotations: [] }
      const rotationsList = data?.rotations || [];
      
      console.log('🗓️ [CALENDAR] Rotations extracted:', rotationsList.length);
      if (rotationsList.length > 0) {
        console.log('🗓️ [CALENDAR] First rotation:', rotationsList[0]);
        console.log('🗓️ [CALENDAR] Sample dates:', {
          start: rotationsList[0]?.rotation_start_date,
          end: rotationsList[0]?.rotation_end_date
        });
      }

      setRotations(rotationsList);
    } catch (err) {
      console.error('Failed to load rotations:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load rotations');
      setRotations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform rotations to calendar events
  const events = useMemo(() => {
    return rotations.map(rotation => {
      const startDate = rotation.rotation_start_date || rotation.start_date;
      const endDate = rotation.rotation_end_date || rotation.end_date;
      
      if (!startDate || !endDate) return null;

      const isValid = rotation.is_valid || rotation.validation_status === 'valid';
      const isPending = rotation.validation_status === 'pending';

      return {
        id: rotation.id,
        title: `Rotation ${rotation.rotation_number || rotation.rotation_id}`,
        start: new Date(startDate),
        end: new Date(endDate),
        resource: {
          rotation,
          status: isValid ? 'valid' : isPending ? 'pending' : 'invalid',
          creditHours: rotation.total_credit_hours,
          blockHours: rotation.total_block_hours
        }
      };
    }).filter(Boolean);
  }, [rotations]);

  const handleSelectEvent = (event) => {
    navigate(`/rotation-detail?id=${event.id}`);
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleUploadSuccess = () => {
    setIsUploadModalOpen(false);
    loadRotations();
  };

  // Custom event style getter
  const eventStyleGetter = (event) => {
    const status = event.resource?.status;
    
    let backgroundColor = '#3b82f6'; // default blue
    let borderColor = '#2563eb';

    if (status === 'valid') {
      backgroundColor = '#22c55e'; // green
      borderColor = '#16a34a';
    } else if (status === 'invalid') {
      backgroundColor = '#ef4444'; // red
      borderColor = '#dc2626';
    } else if (status === 'pending') {
      backgroundColor = '#eab308'; // yellow
      borderColor = '#ca8a04';
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderRadius: '4px',
        color: 'white',
        fontSize: '0.875rem',
        fontWeight: '500',
        padding: '2px 6px'
      }
    };
  };

  // Custom toolbar
  const CustomToolbar = (toolbar) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToToday = () => {
      toolbar.onNavigate('TODAY');
    };

    const label = () => {
      const date = moment(toolbar.date);
      return (
        <span className="text-xl font-bold text-foreground">
          {date.format('MMMM YYYY')}
        </span>
      );
    };

    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goToBack}>
            <Icon name="ChevronLeft" className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goToNext}>
            <Icon name="ChevronRight" className="w-4 h-4" />
          </Button>
          {label()}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            onClick={() => setIsUploadModalOpen(true)}
          >
            Upload Rotation
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-6 md:py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading calendar...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="lg:ml-60 min-h-screen">
        <div className="container-custom py-6 md:py-8">
          <Breadcrumb items={breadcrumbItems} />

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Rotation Calendar
            </h1>
            <p className="text-sm text-muted-foreground">
              View and manage your flight rotations in calendar format
            </p>
          </div>

          {/* Legend */}
          <div className="card mb-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-success border-2 border-success"></div>
                <span className="text-sm text-muted-foreground">Valid</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-warning border-2 border-warning"></div>
                <span className="text-sm text-muted-foreground">Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-error border-2 border-error"></div>
                <span className="text-sm text-muted-foreground">Invalid</span>
              </div>
              <div className="ml-auto text-sm text-muted-foreground">
                {rotations.length} rotation{rotations.length !== 1 ? 's' : ''} found
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="card p-0 overflow-hidden">
            <div className="p-6">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 700 }}
                onSelectEvent={handleSelectEvent}
                onNavigate={handleNavigate}
                view={view}
                onView={setView}
                eventPropGetter={eventStyleGetter}
                components={{
                  toolbar: CustomToolbar
                }}
                popup
                tooltipAccessor={(event) => {
                  const { creditHours, blockHours } = event.resource;
                  return `${event.title}\nCredit: ${creditHours?.toFixed(1) || 0} hrs\nBlock: ${blockHours?.toFixed(1) || 0} hrs`;
                }}
              />
            </div>
          </div>

          {error && (
            <div className="mt-6 flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0" />
              <div className="text-sm text-foreground">
                <strong>Error:</strong> {error}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      <UnifiedUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default CalendarView;
